import type { IconSize } from '#ui/MaterialIcon.js';

import { useRive, useStateMachineInput } from '@rive-app/react-canvas-lite';
import React, { useEffect } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { IconSizes } from '#ui/MaterialIcon.js';

export interface LoaderProps {
  size?: IconSize;
  /** Width of the loader animation */
  width?: number | string;
  /** Height of the loader animation */
  height?: number | string;
  /** CSS class name for additional styling */
  className?: string;
  /** Whether the thinking state is active (controls the "thinking" input binding) */
  thinking?: boolean;
  /** Whether the loading state is active (controls the "loading" input binding, takes precedence over thinking) */
  loading?: boolean;
}

/**
 * A loader component that displays a Rive animation from assets/loader.riv
 */
export const Loader: React.FC<LoaderProps> = ({
  size = 'lg',
  width = typeof size === 'number' ? size : IconSizes[size],
  height = typeof size === 'number' ? size : IconSizes[size],
  className,
  thinking = false,
  loading = false,
}) => {
  const { RiveComponent, rive } = useRive({ src: '/loader.riv', stateMachines: 'Thinker', autoplay: true });
  const thinkingInput = useStateMachineInput(rive, 'Thinker', 'thinking', thinking);
  const loadingInput = useStateMachineInput(rive, 'Thinker', 'loading', loading);

  // Update the thinking input when the prop changes
  useEffect(() => {
    if (thinkingInput) {
      thinkingInput.value = thinking;
    }
  }, [thinking, thinkingInput]);

  // Update the loading input when the prop changes
  useEffect(() => {
    if (loadingInput) {
      loadingInput.value = loading;
    }
  }, [loading, loadingInput]);

  return (
    <div className={cn('flex items-center justify-center', className)}>
      <RiveComponent
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          height: typeof height === 'number' ? `${height}px` : height,
        }}
      />
    </div>
  );
};

export default Loader;
